import GenericTable from 'modules/Tables'
import BarChart from 'modules/Tables/Dashboard/AllDashboards/BarChart'

import { IModel } from 'interfaces/model.interface'
import {
  INavigationPages,
  ISingleNavigationPage,
  defaultDashboardConfig,
} from 'interfaces/navigationPage.interface'

import { InfluenzaSlugs } from './InfluenzaMenu'
import {
  InfluenzaApprovalsModel,
  InfluenzaCandidatesAllCombinedModel,
  InfluenzaCandidatesCombinationVaccinesModel,
  InfluenzaCandidatesNonVaccinesModels,
  InfluenzaCandidatesVaccineModel,
  InfluenzaMediaModel,
  InfluenzaPressReleasesModel,
  InfluenzaTrialsModel,
  NonVaccinesResultsModel,
  VaccinesResultsModel,
} from './InfluenzaModels'
import barChartMessages from 'modules/Tables/Dashboard/AllDashboards/BarChart/message'
import { fetchCollection } from 'api/useCollectionData'
import { RespiratoryVaccinesModel } from 'config/common/baseModel/respiratory360/RespiratoryCandidates'

type InfluenzaNavigationPage<TModel extends IModel<any>> =
  ISingleNavigationPage<InfluenzaSlugs, TModel>

const InfluenzaCandidatesVaccine: InfluenzaNavigationPage<
  typeof RespiratoryVaccinesModel
> = {
  key: 'InfluenzaCandidatesVaccine',
  path: ['candidates', 'dashboard'],
  model: {
    ...InfluenzaCandidatesVaccineModel,
    newNote: {
      text: barChartMessages,
      newUntil: new Date(2024, 8, 25),
    },
  },
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'influenza_vaccines_and_treatments',
      airtableBase: 'lzdb',
      overrideModel: () => ({
        ...InfluenzaCandidatesAllCombinedModel,
        name: 'All',
        customAPIFunction: (axios) => {
          const resolveData = Promise.all([
            fetchCollection(axios, 'influenza', 'candidate', {
              airtableBase: 'lzdb',
              view: 'influenza_vaccines',
            }),
            fetchCollection(axios, 'influenza', 'candidate', {
              airtableBase: 'lzdb',
              view: 'influenza_treatments',
            }),
          ]).then((d) => {
            return d[0].results
              .map((obj: any) => {
                const { treatmentType, vaccineType, ...rest } = obj
                return {
                  ...rest,
                  vaccineType: treatmentType ?? vaccineType,
                }
              })
              .concat(d[1].results)
          })

          return () => resolveData
        },
      }),
    },
    {
      name: 'Vaccines',
      airtableName: 'influenza_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'influenza_treatments',
      airtableBase: 'lzdb',
      overrideModel: (model) => ({
        ...model,
        ...InfluenzaCandidatesNonVaccinesModels,
        customAPIFunction: (axios) => {
          const resolveData = Promise.all([
            fetchCollection(axios, 'influenza', 'candidate', {
              airtableBase: 'lzdb',
              view: 'influenza_treatments',
            }),
          ]).then((d) => {
            return d[0].results.map((obj: any) => {
              const { treatmentType, ...rest } = obj
              return {
                ...rest,
                vaccineType: treatmentType,
              }
            })
          })

          return () => resolveData
        },
      }),
    },
    {
      name: 'Combination vaccines',
      airtableName: 'influenza_combination_vaccines',
      airtableBase: 'lzdb',
      overrideModel: (model) => ({
        ...InfluenzaCandidatesCombinationVaccinesModel,
      }),
    },
    {
      name: 'Seasonal Vaccines',
      airtableName: 'influenza_vaccines_seasonal',
      airtableBase: 'lzdb',
      overrideModel: (model) => ({
        ...InfluenzaCandidatesVaccineModel,
      }),
    },
    {
      name: 'Pandemic Vaccines',
      airtableName: 'influenza_vaccines_pandemic',
      airtableBase: 'lzdb',
      overrideModel: (model) => ({
        ...InfluenzaCandidatesVaccineModel,
      }),
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    render: BarChart,
    verticalAxisLabel: 'Number of Candidates',
    filterBlacklistOptions: [
      'sponsors',
      'alternativeNames',
      'mechanismOfActionDescription',
    ],
    defaultHorizontalFilterValue: {
      label: 'Phase',
      value: 'latestPhaseManual',
    },
    defaultStackByFilterValue: {
      label: 'Technology Type',
      value: 'technologyType',
    },
    sortingOrders: {
      latestPhaseManual: [
        'Preclinical',
        'Phase 0',
        'Phase I',
        'Phase I/II',
        'Phase II',
        'Phase II/III',
        'Phase III',
        'Approved',
        'Phase IV',
        'Discontinued',
        'Paused',
        'Inactive',
        'Not Applicable',
        'Unknown',
      ],
    },
  },
}

const InfluenzaTrials: InfluenzaNavigationPage<typeof InfluenzaTrialsModel> = {
  key: 'InfluenzaTrials',
  path: ['trials', 'all-data'],
  model: InfluenzaTrialsModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'influenza_all',
      airtableBase: 'lzdb',
    },
    {
      name: 'Vaccines',
      airtableName: 'influenza_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'influenza_treatments',
      airtableBase: 'lzdb',
    },
  ],
}
const InfluenzaNewsPressReleases: InfluenzaNavigationPage<
  typeof InfluenzaPressReleasesModel
> = {
  key: 'InfluenzaNewsPressReleases',
  path: null,
  model: InfluenzaPressReleasesModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'front_end_export_influenza_default_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Vaccines',
      airtableName: 'front_end_export_influenza_vaccines_view',
      airtableBase: 'respiratory360',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'front_end_export_influenza_treatments_view',
      airtableBase: 'respiratory360',
    },
  ],
}
const InfluenzaNewsMedia: InfluenzaNavigationPage<typeof InfluenzaMediaModel> =
  {
    key: 'InfluenzaNewsMedia',
    path: null,
    model: InfluenzaMediaModel,
    component: GenericTable,
    views: [
      {
        name: 'Default',
        airtableName: 'front_end_export_influenza_default_view',
        airtableBase: 'respiratory360',
      },
      {
        name: 'Vaccines',
        airtableName: 'front_end_export_influenza_vaccines_view',
        airtableBase: 'respiratory360',
      },
      {
        name: 'Non-Vaccines',
        airtableName: 'front_end_export_influenza_treatments_view',
        airtableBase: 'respiratory360',
      },
    ],
  }

const InfluenzaVaccineResults: InfluenzaNavigationPage<
  typeof VaccinesResultsModel
> = {
  key: 'InfluenzaVaccineResults',
  path: ['results', 'vaccines'],
  model: VaccinesResultsModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'Vaccines',
      airtableBase: 'respiratory360',
    },
  ],
}

const InfluenzaNonVaccineResults: InfluenzaNavigationPage<
  typeof NonVaccinesResultsModel
> = {
  key: 'InfluenzaNonVaccineResults',
  path: ['results', 'non-vaccines'],
  model: NonVaccinesResultsModel,
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'Non-vaccines',
      airtableBase: 'respiratory360',
    },
  ],
}

const InfluenzaApprovals: InfluenzaNavigationPage<
  typeof InfluenzaApprovalsModel
> = {
  key: 'InfluenzaApprovals',
  path: ['regulatory', 'table'],
  model: InfluenzaApprovalsModel,
  component: GenericTable,
  views: [
    {
      name: 'Vaccines',
      airtableName: 'influenza_regulatory_vaccines',
      airtableBase: 'lzdb',
    },
    {
      name: 'Non-Vaccines',
      airtableName: 'influenza_regulatory_treatments',
      airtableBase: 'lzdb',
    },
  ],
}

export const InfluenzaPages: INavigationPages<InfluenzaSlugs> = {
  InfluenzaCandidatesVaccine,
  InfluenzaTrials,
  InfluenzaNewsPressReleases,
  InfluenzaNewsMedia,
  InfluenzaVaccineResults,
  InfluenzaNonVaccineResults,
  InfluenzaApprovals,
}
